/**
 * FleetImportReview — Orchestrates the fleet import review experience.
 *
 * Combines FleetTimeline, DepotDemandChart, FleetImportTable,
 * VehicleMatchCard, and FleetImportActions.
 *
 * Props:
 *   extractionResult: raw extraction result from Gemini + post-processing
 *   vehicleDatabase: array of vehicles from DB
 *   onImport: (fleetEntries) => void — called with fleet-ready data
 *   onCancel: () => void
 */
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { SectionCard } from '@vortx/shared-ui';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FleetTimeline from './FleetTimeline';
import DepotDemandChart from './DepotDemandChart';
import FleetImportTable from './FleetImportTable';
import VehicleMatchCard from './VehicleMatchCard';
import FleetImportActions from './FleetImportActions';

function FleetImportReview({
    extractionResult,
    vehicleDatabase = [],
    onImport,
    onCancel,
}) {
    const [vehicles, setVehicles] = useState(
        () => extractionResult?.vehicles || []
    );
    const [selectedVehicleId, setSelectedVehicleId] = useState(
        () => extractionResult?.vehicle_match?.best_match?.id || null
    );
    const [importing, setImporting] = useState(false);
    const tableRef = useRef(null);

    const vehicleSpecs = extractionResult?.vehicle_specs || {};
    const vehicleMatch = extractionResult?.vehicle_match || {};
    const fleetName = extractionResult?.fleet_name || 'Imported Fleet';

    const handleVehicleUpdate = useCallback((index, updated) => {
        setVehicles((prev) => {
            const next = [...prev];
            next[index] = updated;
            return next;
        });
    }, []);

    const handleVehicleClick = useCallback((index) => {
        // Scroll to the flagged row in the table
        const row = document.getElementById(`fleet-import-row-${index}`);
        if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            row.style.outline = '2px solid #ed6c02';
            setTimeout(() => { row.style.outline = ''; }, 2000);
        }
    }, []);

    const flagCount = useMemo(
        () => vehicles.filter((v) =>
            v.depot_availability?.some((w) => w.available_from === 'MISSING')
        ).length,
        [vehicles]
    );

    const handleImport = useCallback(async () => {
        setImporting(true);
        try {
            // Build fleet entries from reviewed data
            const selectedVehicle = vehicleDatabase.find((v) => v.id === selectedVehicleId);
            const now = Date.now();

            const fleetEntries = vehicles.map((v, i) => ({
                id: `imported_${(v.route_name || v.route_id || i).toString().toLowerCase().replace(/\s+/g, '_')}_${now}`,
                source: 'fleet_operations_import',
                route_name: v.route_name || null,
                route_id: v.route_id || null,
                vehicle_id: selectedVehicleId || null,
                vehicle_name: selectedVehicle?.name || null,
                quantity: 1,
                daily_miles: v.daily_miles || 0,
                departure_time: v.depot_availability?.[v.depot_availability.length - 1]?.available_until || null,
                return_time: v.depot_availability?.[0]?.available_from || null,
                battery_capacity_kwh: selectedVehicle?.battery_kwh || vehicleSpecs.battery_capacity_kwh || 0,
                efficiency_kwh_per_mile: selectedVehicle?.efficiency_kwh_per_mile || vehicleSpecs.efficiency_kwh_per_mile || 0,
                max_ac_charge_kw: selectedVehicle?.max_ac_charge_kw || 0,
                max_dc_charge_kw: selectedVehicle?.max_dc_charge_kw || vehicleSpecs.charger_power_kw || 0,
                depot_availability: v.depot_availability || [],
            }));

            await onImport(fleetEntries);
        } finally {
            setImporting(false);
        }
    }, [vehicles, selectedVehicleId, vehicleDatabase, vehicleSpecs, onImport]);

    return (
        <SectionCard
            title={`Import: ${fleetName}`}
            icon={<LocalShippingIcon />}
        >
            {/* Vehicle & Charger Match */}
            <VehicleMatchCard
                vehicleSpecs={vehicleSpecs}
                vehicleMatch={vehicleMatch}
                vehicleDatabase={vehicleDatabase}
                selectedVehicleId={selectedVehicleId}
                onVehicleSelect={setSelectedVehicleId}
            />

            {/* Timeline */}
            <FleetTimeline
                vehicles={vehicles}
                onVehicleClick={handleVehicleClick}
            />

            {/* Depot Demand */}
            <DepotDemandChart vehicles={vehicles} />

            {/* Editable Table */}
            <FleetImportTable
                vehicles={vehicles}
                vehicleSpecs={vehicleSpecs}
                onVehicleUpdate={handleVehicleUpdate}
                tableRef={tableRef}
            />

            {/* Actions */}
            <FleetImportActions
                vehicles={vehicles}
                flagCount={flagCount}
                onImport={handleImport}
                onCancel={onCancel}
                importing={importing}
            />
        </SectionCard>
    );
}

export default FleetImportReview;
